import { useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Popover,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import {
  useImages,
  useLinkImage,
  useUploadImage,
} from '../../api/queries/media';
import { AppImage } from '../common/AppImage';
import { ImagePositionPicker, type ImagePosition } from './ImagePositionPicker';

interface ImageInsertPopoverProps {
  paragraphId: string;
  onGenerateImage: () => void;
}

export function ImageInsertPopover({
  paragraphId,
  onGenerateImage,
}: ImageInsertPopoverProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tab, setTab] = useState(0);
  const [position, setPosition] = useState<ImagePosition>('inline');
  const uploadImage = useUploadImage();
  const linkImage = useLinkImage(paragraphId);
  const { data: libraryImages } = useImages();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const open = Boolean(anchorEl);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const uploaded = await uploadImage.mutateAsync(file);
    if (uploaded) {
      await linkImage.mutateAsync({ imageId: uploaded.id, position });
    }
    setAnchorEl(null);
  };

  const handleSelectFromLibrary = async (imageId: string) => {
    await linkImage.mutateAsync({ imageId, position });
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          opacity: 0,
          transition: 'opacity 0.2s',
          color: 'text.disabled',
          fontSize: 13,
          borderRadius: 1,
          px: 1,
          py: 0.25,
          minWidth: 0,
          '&:hover': { opacity: 1 },
        }}
        className="add-image-btn"
      >
        <AddPhotoAlternateIcon sx={{ fontSize: 16, mr: 0.5 }} />
        Image
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 360, p: 2 } } }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Add Image
        </Typography>
        <ImagePositionPicker value={position} onChange={setPosition} />

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mt: 1, mb: 1, minHeight: 32 }}
        >
          <Tab label="Generate" sx={{ minHeight: 32, py: 0 }} />
          <Tab label="Upload" sx={{ minHeight: 32, py: 0 }} />
          <Tab label="Library" sx={{ minHeight: 32, py: 0 }} />
        </Tabs>

        {tab === 0 && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Generate an image with AI for this paragraph.
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setAnchorEl(null);
                onGenerateImage();
              }}
            >
              Open Generator
            </Button>
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Upload an image from your computer.
            </Typography>
            <input
              type="file"
              accept="image/*"
              hidden
              ref={fileInputRef}
              onChange={handleUpload}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadImage.isPending || linkImage.isPending}
            >
              {uploadImage.isPending ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              Choose File
            </Button>
          </Stack>
        )}

        {tab === 2 && (
          <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
            {!libraryImages || libraryImages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No images in your library yet.
              </Typography>
            ) : (
              <Grid container spacing={1}>
                {libraryImages.map((img) => (
                  <Grid size={{ xs: 4 }} key={img.id}>
                    <AppImage
                      src={img.thumbnailUrl ?? img.url}
                      width="100%"
                      height={80}
                      objectFit="cover"
                      onClick={() => handleSelectFromLibrary(img.id)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 0.5,
                        '&:hover': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </Popover>
    </>
  );
}
